import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, shareReplay, startWith } from 'rxjs';

import { RestBoardModel } from '@main-application/boards/interfaces/board';
import { BoardProgressType } from '@main-application/boards/interfaces/board.enums';
import { LocalStorageDataEnum } from '@shared/enums/local-storage-data.enum';
import { WorkflowStepEnumType } from '@shared/enums/workflow-step.enum';
import { BoardConnection } from '@shared/interfaces/turnover.interface';

const readyColumns = [WorkflowStepEnumType.MoveIn, WorkflowStepEnumType.LeaseUp];

interface MakeReadyCounters {
  notStarted: number;
  started: number;
  lastStep: number;
}

export const defaultMakeReadyCounters: MakeReadyCounters = {
  notStarted: 0,
  started: 0,
  lastStep: 0,
} as const;

@Injectable({
  providedIn: 'root',
})
export class TurnoverKanbanService {
  #hiddenReadyColumns$ = new BehaviorSubject<Set<WorkflowStepEnumType>>(new Set());
  hiddenReadyColumns$ = this.#hiddenReadyColumns$.pipe(shareReplay(1));

  #hiddenNotReadyColumns$ = new BehaviorSubject<Set<WorkflowStepEnumType>>(new Set());
  hiddenNotReadyColumns$ = this.#hiddenNotReadyColumns$.pipe(shareReplay(1));

  hiddenColumns$ = combineLatest([this.hiddenReadyColumns$, this.hiddenNotReadyColumns$]).pipe(
    map(([hiddenReadyColumns, hiddenNotReadyColumns]) => [...hiddenReadyColumns, ...hiddenNotReadyColumns]),
    shareReplay(1)
  );

  extendedMakeReadyColumn$ = combineLatest([this.hiddenReadyColumns$, this.hiddenNotReadyColumns$]).pipe(
    map(([hiddenReadyColumns, hiddenNotReadyColumns]) => hiddenNotReadyColumns.size + hiddenReadyColumns.size > 2),
    startWith(false),
    distinctUntilChanged(),
    shareReplay(1)
  );

  #makeReadyCounters$ = new BehaviorSubject<Map<number, MakeReadyCounters>>(new Map());
  makeReadyCounters$ = this.#makeReadyCounters$.asObservable();

  addMakeReadyCounters(sectionId: number, counters: MakeReadyCounters) {
    const current = this.#makeReadyCounters$.getValue();
    const existing = current.get(sectionId);
    if (
      existing &&
      existing.notStarted === counters.notStarted &&
      existing.started === counters.started &&
      existing.lastStep === counters.lastStep
    ) {
      return;
    }
    const newMap = new Map(current);
    newMap.set(sectionId, counters);
    this.#makeReadyCounters$.next(newMap);
  }

  resetMakeReadyCounters() {
    this.#makeReadyCounters$.next(new Map());
  }

  setupHiddenColumns(hiddenColumns: WorkflowStepEnumType[] = []) {
    const hiddenReadyColumns = hiddenColumns.filter(column => readyColumns.includes(column));
    const hiddenNotReadyColumns = hiddenColumns.filter(column => !readyColumns.includes(column));
    this.#hiddenReadyColumns$.next(new Set(hiddenReadyColumns));
    this.#hiddenNotReadyColumns$.next(new Set(hiddenNotReadyColumns));
  }

  clearHiddenColumns() {
    this.#hiddenReadyColumns$.next(new Set());
    this.#hiddenNotReadyColumns$.next(new Set());
  }

  getExpandedSections(userId: number): number[] {
    return (JSON.parse(localStorage.getItem(this.getFieldName(userId))) as number[]) || [];
  }

  setExpandedSection(userId: number, sectionsId: number[]) {
    localStorage.setItem(this.getFieldName(userId), JSON.stringify(sectionsId));
  }

  private getFieldName(userId: number): string {
    return `${LocalStorageDataEnum.KANBAN_ACTIVE_SECTIONS}_${userId}`;
  }

  findBoardConnection(
    boardList: RestBoardModel[],
    boardConnections: BoardConnection[],
    progressType: BoardProgressType,
    portfolioId: number
  ): BoardConnection | undefined {
    const allBoardIds = boardConnections?.filter(c => c.boardProgressType === progressType)?.map(c => c.boardId);
    const boards = boardList?.filter(b => allBoardIds?.includes(b.id));
    if (!boards?.length) {
      return undefined;
    }
    const board = boards?.find(b => b.selectedPortfolioIds?.includes(portfolioId.toString())) || boards[0];
    return boardConnections?.find(c => c.boardId === board?.id);
  }

  getBoardConnection(
    step: WorkflowStepEnumType,
    boardConnections: BoardConnection[],
    boards: RestBoardModel[],
    portfolioId: number
  ): BoardConnection | undefined {
    let boardConnection: BoardConnection | undefined;
    if (step === WorkflowStepEnumType.MakeReady) {
      boardConnection = this.findBoardConnection(
        boards,
        boardConnections,
        BoardProgressType.UpdateMakeReadyProgressBar,
        portfolioId
      );
    }
    if (step === WorkflowStepEnumType.Reno) {
      boardConnection = this.findBoardConnection(
        boards,
        boardConnections,
        BoardProgressType.UpdateRENOProgressBar,
        portfolioId
      );
    }
    if (step === WorkflowStepEnumType.Notice) {
      boardConnection = this.findBoardConnection(
        boards,
        boardConnections,
        BoardProgressType.UpdateNoticeProgressBar,
        portfolioId
      );
    }
    if (step === WorkflowStepEnumType.MoveIn) {
      boardConnection = this.findBoardConnection(
        boards,
        boardConnections,
        BoardProgressType.UpdateMoveInProgressBar,
        portfolioId
      );
    }
    if (step === WorkflowStepEnumType.MoveOut) {
      boardConnection = this.findBoardConnection(
        boards,
        boardConnections,
        BoardProgressType.UpdateMoveOutProgressBar,
        portfolioId
      );
    }
    if (step === WorkflowStepEnumType.Approval) {
      boardConnection = this.findBoardConnection(
        boards,
        boardConnections,
        BoardProgressType.UpdateApprovalProgressBar,
        portfolioId
      );
    }
    if (step === WorkflowStepEnumType.LeaseUp) {
      boardConnection = this.findBoardConnection(
        boards,
        boardConnections,
        BoardProgressType.UpdateLeaseUpProgressBar,
        portfolioId
      );
    }
    if (boardConnection && !boardConnection.boardName) {
      const board = boards.find(x => x.id === boardConnection?.boardId);
      return { ...boardConnection, boardName: board?.name || '' };
    }
    return boardConnection;
  }
}
